import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Alert from '@material-ui/lab/Alert';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';

import useApi from 'app/context/useApi';
import EditTicketDialog from 'app/components/event/EditTicketDialog';

const useStyles = makeStyles(theme => ({

    section: {
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginTopSmall: {
        marginTop: theme.spacing(1)
    },
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    list: {
        backgroundColor: theme.palette.grey[200],
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(3),
        padding: theme.spacing(3)
    },
    listItem: {
        backgroundColor: '#fff',
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(3),
        display: 'block'
    },
    textPrimary: {
        color: theme.palette.primary.main
    },
    textInfo: {
        color: theme.palette.info.main
    },
    textWarning: {
        color: theme.palette.warning.main
    },
    textError: {
        color: theme.palette.error.main
    },
    nowrap: {
        whiteSpace: 'nowrap'
    }
}));



export default function Tickets(props) {

    const {
        event,
        tickets,
        getEvent,
        setLoading
    } = props;

    const api = useApi();

    const [ ticketDialogOpen, setTicketDialogOpen ] = useState(false);
    const [ editingTicket, setEditingTicket ] = useState(null);

    const classes = useStyles();
    const theme = useTheme();

    // Ticket Dialog Functions

    const openTicketDialog = (id) => {
        if (id > 0) {
            const ticket = tickets.find(ticket => ticket.id === id);
            setEditingTicket(ticket);
        }

        setTicketDialogOpen(true)
    }

    const closeTicketDialog = () => {
        setTicketDialogOpen(false);
    }

    const closeTicketDialogAndRefresh = () => {
        closeTicketDialog();
        getEvent();
    }

    const ticketDialogClosed = () => {
        setEditingTicket(null);
    }

    const deleteTicket = (id) => {

        setLoading(true);

        api.delete(`/admin/ticket/${id}`)
            .then( () => getEvent())
            .catch(error => {
                console.log(error.response);
            }).then( () => setLoading(false));
    }

    const getAmountClass = t => {
        const percentLeft = ((t.amount_left / t.amount).toFixed(2) * 100);

        if (percentLeft > 20) return classes.textPrimary;
        if (percentLeft > 10) return classes.textInfo;
        if (percentLeft > 5) return classes.textWarning;
        return classes.textError;
    }

    const getChipLabel = t => {
        switch (t.type) {
            case 'admin': return 'Admin Only';
            case 'both': return 'Group and Guest';
            case 'group': return 'Group Only';
            case 'guest': return 'Guest Only'
        }
    }


    return (
        <form noValidate autoComplete="off" className={classes.section}>
            <Grid container
                alignItems="center"
                justify="space-between"
                spacing={3}
                >
                <Grid item>
                    <Typography variant="h6" component="h3">
                        Event Tickets
                    </Typography>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => openTicketDialog(0)}
                        startIcon={<AddIcon />}
                        >
                        NEW TICKET
                    </Button>
                </Grid>
            </Grid>
            <Typography color="textSecondary">
                Click on a ticket to edit the details, or add a new ticket type.
            </Typography>
            {tickets.length ? (
                <List className={classes.list}>
                    {tickets.map((ticket, index) => (
                        <ListItem key={index} className={classes.listItem}>
                            <Grid container wrap="nowrap" alignItems="center" spacing={3}>
                                <Grid item>
                                    <Typography variant="h6" color="primary">
                                        {ticket.title}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Chip label={getChipLabel(ticket)} />
                                </Grid>
                                <Grid item className={classes.nowrap}>
                                    <Tooltip title="Edit">
                                        <IconButton onClick={() => openTicketDialog(ticket.id)}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton onClick={()=>deleteTicket(ticket.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Typography variant="body1" color="textSecondary" gutterBottom>
                                {ticket.description}
                            </Typography>
                            <Grid container direction="row" justify="space-between" alignItems='center'>
                                <Grid item>
                                    <Typography variant="body2" color="textSecondary">Price: <strong>${ticket.price}</strong></Typography>
                                </Grid>
                                {(ticket.amount == 0) ? (
                                    <Grid item>
                                        <Typography variant="body2" color="textSecondary">Unlimited Tickets</Typography>
                                    </Grid>
                                ) : (
                                    <Grid item>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Tickets Left: <Typography component="span" variant="subtitle1" className={getAmountClass(ticket)}>{ticket.amount_left}</Typography>/{ticket.amount}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                            {ticket.late_by &&
                                <Grid container direction="row" justify="space-between" alignItems='center'>
                                    <Grid item >
                                        <Typography variant="body2" color="textSecondary">
                                            Late Price:  <strong>${ticket.late_price}</strong>
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2" color="textSecondary">
                                            Late Price Begins:  <strong>{DateTime.fromISO(ticket.late_by).toLocaleString(DateTime.DATETIME_MED)}</strong>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            }
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Alert severity="warning" className={classes.marginTop}>
                    No ticket types have been created for this event.
                </Alert>
            )}
            { event.group_tickets_required ? (
                <Alert severity="info" className={classes.marginTop}>
                    Tickets are required for group members to attend this event.
                </Alert>
            ) : (
                <Alert severity="info" className={classes.marginTop}>
                    Tickets are not required for group members to attend this event.
                </Alert>
            )}
            {event.invitations_active && (
                <Alert severity="info" className={classes.marginTopSmall}>
                    {event.guest_tickets_required ? (
                        <span>Tickets are required for invited guests to attend this event.</span>
                    ) : (
                        <span>Tickets are not required for invited guests to attend this event.</span>
                    )}
                </Alert>
            )}
            <EditTicketDialog
                open={ticketDialogOpen}
                ticket={editingTicket}
                event={event}
                onUpdate={closeTicketDialogAndRefresh}
                onClose={closeTicketDialog}
                onClosed={ticketDialogClosed}
            />
        </form>
    );
}
